/**
 * CLI configuration persistence — thin wrapper around @arbidocs/sdk FileConfigStore.
 *
 * Provides module-level functions that delegate to the shared store instance.
 * The `require*` methods catch ArbiError and call process.exit(1) for CLI behavior.
 */

import chalk from 'chalk'
import { FileConfigStore, ArbiError } from '@arbidocs/sdk'
import type { CliConfig, CliCredentials, ChatSession } from '@arbidocs/sdk'

export type { CliConfig, CliCredentials, ChatSession }

/** Shared store instance used by all CLI commands */
export const store = new FileConfigStore()

// ── Config ───────────────────────────────────────────────────────────────────

export function getConfig(): CliConfig | null {
  return store.getConfig()
}

export function saveConfig(config: CliConfig): void {
  store.saveConfig(config)
}

export function updateConfig(updates: Partial<CliConfig>): void {
  store.updateConfig(updates)
}

export function requireConfig(): CliConfig {
  try {
    return store.requireConfig()
  } catch (err) {
    if (err instanceof ArbiError) {
      console.error(err.message)
      process.exit(1)
    }
    throw err
  }
}

/**
 * Resolve config with auto-detection fallbacks.
 * Tries: config file → ARBI_SERVER_URL env → .env → public/config.json → https://localhost.
 * Prints a dim hint when auto-detecting so the user knows where config came from.
 */
export function resolveConfig(): CliConfig {
  const { config, source } = store.resolveConfigWithFallbacks()
  if (source !== 'config') {
    // Use stderr to avoid polluting piped output
    console.error(chalk.dim(`Using server URL from ${source}: ${config.baseUrl}`))
  }
  return config
}

// ── Credentials ──────────────────────────────────────────────────────────────

export function getCredentials(): CliCredentials | null {
  return store.getCredentials()
}

export function saveCredentials(creds: CliCredentials): void {
  store.saveCredentials(creds)
}

export function deleteCredentials(): void {
  store.deleteCredentials()
}

export function requireCredentials(): CliCredentials {
  try {
    return store.requireCredentials()
  } catch (err) {
    if (err instanceof ArbiError) {
      console.error(err.message)
      process.exit(1)
    }
    throw err
  }
}

// ── Last metadata (citation browsing) ────────────────────────────────────────

export function saveLastMetadata(metadata: unknown): void {
  store.saveLastMetadata?.(metadata)
}

export function loadLastMetadata(): unknown | null {
  return store.loadLastMetadata?.() ?? null
}

// ── Chat session ─────────────────────────────────────────────────────────────

export function getChatSession(): ChatSession {
  return store.getChatSession()
}

export function saveChatSession(session: ChatSession): void {
  store.saveChatSession(session)
}

export function updateChatSession(updates: Partial<ChatSession>): void {
  store.updateChatSession(updates)
}

export function clearChatSession(): void {
  store.clearChatSession()
}
